import Loading from '../../../components/Loading';
import {Dialog as LumaAccessibleDialog, DialogPanel as LumaAccessiblePanel, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Eye, Pencil, Shield, UserCheck } from 'lucide-react';
import { fetchCorrespondenciasPorCita, Correspondencia } from '../../../services/correspondencia';
import EditarCorrespondenciaModal from './EditarCorrespondenciaModal';
import Pagination from '../../../components/Pagination/Pagination';

interface HistorialCorrespondenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
    citaId: number;
    citaCodigo: string;
}

const HistorialCorrespondenciaModal: React.FC<HistorialCorrespondenciaModalProps> = ({
    isOpen,
    onClose,
    citaId,
    citaCodigo
}) => {
    const [correspondencias, setCorrespondencias] = useState<Correspondencia[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState<string>('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [correspondenciaEditando, setCorrespondenciaEditando] = useState<Correspondencia | null>(null);
    const [verFotoModal, setVerFotoModal] = useState<string | null>(null);

    const loadCorrespondencias = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchCorrespondenciasPorCita(citaId);
            setCorrespondencias(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setIsLoading(false);
        }
    }, [citaId]);

    useEffect(() => {
        if (isOpen && citaId) {
            loadCorrespondencias();
        }
    }, [isOpen, citaId, loadCorrespondencias]);

    const formatDateTime = (dateString: string | null): string => {
        if (!dateString) return 'Pendiente';

        const date = new Date(dateString);
        return date.toLocaleString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    const getEstadoColor = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'notificado':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'entregado':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'cancelado':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getEstadoTexto = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return 'Recibido';
            case 'notificado':
                return 'Notificado';
            case 'entregado':
                return 'Entregado';
            case 'cancelado':
                return 'Cancelado';
            default:
                return estado;
        }
    };

    const correspondenciasFiltradas = correspondencias.filter(correspondencia => {
        if (filtroEstado !== 'todos' && correspondencia.estado !== filtroEstado) {
            return false;
        }

        if (busqueda.trim()) {
            const searchTerm = busqueda.toLowerCase();
            return (
                (correspondencia.codigo && correspondencia.codigo.toLowerCase().includes(searchTerm)) ||
                (correspondencia.descripcion && correspondencia.descripcion.toLowerCase().includes(searchTerm)) ||
                correspondencia.remitente_nombre.toLowerCase().includes(searchTerm) ||
                correspondencia.remitente_apellido.toLowerCase().includes(searchTerm) ||
                correspondencia.remitente_ci_nit.toLowerCase().includes(searchTerm) ||
                (correspondencia.receptor_nombre && correspondencia.receptor_nombre.toLowerCase().includes(searchTerm))
            );
        }

        return true;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [busqueda, filtroEstado]);

    const totalPages = Math.ceil(correspondenciasFiltradas.length / itemsPerPage) || 1;
    const paginatedItems = correspondenciasFiltradas.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                .historial-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .historial-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .historial-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 127, 200, 0.3);
                    border-radius: 20px;
                }
                .historial-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 127, 200, 0.5);
                }
                .historial-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(0, 127, 200, 0.3) transparent;
                }
            `}</style>

            {/* Modal de foto en pantalla completa */}
            {verFotoModal && (
                <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true"
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4 cursor-pointer backdrop-blur-sm"
                    onClick={() => setVerFotoModal(null)}
                >
                    <LumaAccessiblePanel data-luma-dialog="true" className="relative max-w-3xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setVerFotoModal(null)}
                            className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img src={verFotoModal} alt="Foto Ampliada" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                    </LumaAccessiblePanel>
                </LumaAccessibleDialog>
            )}

            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto historial-scroll">
                    <div className="p-6 md:p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <LumaAccessibleTitle className="text-2xl font-bold text-gray-800">Historial de Correspondencia</LumaAccessibleTitle>
                                <p className="text-gray-600 mt-1 text-sm">
                                    Cita <span className="font-semibold text-[#3564db]">{citaCodigo}</span> - {correspondencias.length} correspondencia(s)
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Filtros y búsqueda */}
                        <div className="flex flex-col md:flex-row gap-4 mb-6">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por código, remitente, receptor o descripción..."
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3564db]/20 focus:border-[#3564db] w-full text-sm"
                                />
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={filtroEstado}
                                    onChange={(e) => setFiltroEstado(e.target.value)}
                                    className="px-3.5 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3564db]/20 focus:border-[#3564db] text-sm"
                                >
                                    <option value="todos">Todos los estados</option>
                                    <option value="recibido">Recibido</option>
                                    <option value="notificado">Notificado</option>
                                    <option value="entregado">Entregado</option>
                                    <option value="cancelado">Cancelado</option>
                                </select>
                            </div>
                        </div>

                        {isLoading ? (
                            <Loading compact text="Cargando correspondencias…"/>
                        ) : paginatedItems.length > 0 ? (
                            <div className="space-y-4">
                                {paginatedItems.map((correspondencia) => (
                                    <div key={correspondencia.id} className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-all">
                                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
                                            {/* Foto / Miniatura si existe */}
                                            {correspondencia.imagen && (
                                                <div
                                                    className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 cursor-pointer flex-shrink-0 border border-gray-200 relative group"
                                                    onClick={() => setVerFotoModal(correspondencia.imagen || null)}
                                                >
                                                    <img
                                                        src={correspondencia.imagen}
                                                        alt="Paquete"
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                    />
                                                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                                                        <Eye className="w-4 h-4" />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex-1 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <span className="px-3 py-1 text-sm font-mono font-bold bg-blue-50 text-[#3564db] rounded-lg border border-blue-200">
                                                            {correspondencia.codigo || `#${correspondencia.id}`}
                                                        </span>
                                                        <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getEstadoColor(correspondencia.estado)}`}>
                                                            {getEstadoTexto(correspondencia.estado)}
                                                        </span>
                                                        {correspondencia.pin_seguridad && correspondencia.estado !== 'entregado' && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono font-bold bg-slate-100 text-slate-700 rounded-md">
                                                                <Shield className="w-3 h-3 text-[#3564db]" />
                                                                PIN: {correspondencia.pin_seguridad}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <p className="text-sm font-medium text-gray-800">
                                                    {correspondencia.descripcion || 'Sin descripción'}
                                                </p>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 text-xs pt-1 border-t border-gray-100">
                                                    <div>
                                                        <span className="text-gray-400 block mb-0.5">Remitente:</span>
                                                        <p className="font-semibold text-gray-700">{correspondencia.remitente_nombre} {correspondencia.remitente_apellido}</p>
                                                        <p className="text-gray-500">{correspondencia.remitente_ci_nit}</p>
                                                    </div>

                                                    <div>
                                                        <span className="text-gray-400 block mb-0.5">Recepcionista:</span>
                                                        <p className="font-semibold text-gray-700 flex items-center gap-1">
                                                            <UserCheck className="w-3.5 h-3.5 text-[#3564db]" />
                                                            <span>{correspondencia.usuario_recibe_nombre || 'Recepción Comarca'}</span>
                                                        </p>
                                                    </div>

                                                    <div>
                                                        <span className="text-gray-400 block mb-0.5">Receptor:</span>
                                                        {correspondencia.receptor_nombre ? (
                                                            <>
                                                                <p className="font-semibold text-gray-700">{correspondencia.receptor_nombre} {correspondencia.receptor_apellido}</p>
                                                                <p className="text-gray-500">{correspondencia.receptor_ci_nit}</p>
                                                            </>
                                                        ) : (
                                                            <p className="text-gray-400 italic">No asignado</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <span className="text-gray-400 block mb-0.5">Fecha Recepción:</span>
                                                        <p className="font-medium text-gray-700">{formatDateTime(correspondencia.fecha_recepcion)}</p>
                                                    </div>

                                                    <div>
                                                        <span className="text-gray-400 block mb-0.5">Fecha Entrega:</span>
                                                        <p className="font-medium text-gray-700">{formatDateTime(correspondencia.fecha_entrega)}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 self-start">
                                                <button
                                                    onClick={() => setCorrespondenciaEditando(correspondencia)}
                                                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                                                    title="Editar o Validar Entrega"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                    Gestionar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {correspondenciasFiltradas.length > 0 && (
                                    <div className="pt-2">
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={(page) => setCurrentPage(page)}
                                            itemsPerPage={itemsPerPage}
                                            totalItems={correspondenciasFiltradas.length}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <X className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-800 mb-1">
                                    No hay correspondencias registradas
                                </h3>
                                <p className="text-gray-500 text-sm max-w-md mx-auto">
                                    {busqueda || filtroEstado !== 'todos'
                                        ? 'No se encontraron correspondencias con los filtros aplicados'
                                        : 'Esta cita no tiene correspondencias registradas en el sistema'
                                    }
                                </p>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6 text-xs text-gray-500">
                            <div>
                                Mostrando {correspondenciasFiltradas.length} de {correspondencias.length} correspondencias
                            </div>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                    {correspondenciaEditando && (
                        <EditarCorrespondenciaModal
                            isOpen={!!correspondenciaEditando}
                            onClose={() => setCorrespondenciaEditando(null)}
                            correspondencia={correspondenciaEditando}
                            onSave={async () => {
                                await loadCorrespondencias();
                                setCorrespondenciaEditando(null);
                            }}
                        />
                    )}
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>
    );
};

export default HistorialCorrespondenciaModal;

